import React, { useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  InputAdornment,
  Typography,
  styled,
  Box,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';

import { Visibility, VisibilityOff } from '@material-ui/icons';

import { useMutation } from '@apollo/client';

import { useLocation } from 'react-router-dom';

import { getPasswordValidationState } from './helper/getPasswordValidationState';

import sparkTheme from '@/themes/sparkTheme';

import Button from '@/components/Button';
import { TextField } from '@/components';
import { SAVE_PASSWORD_AND_ACTIVATE_USER } from '@/apollo/mutations';
import { OverrideToggle } from '@/pages/Onboarding/components/OverrideToggle/OverrideToggle';
import { getProgram } from '@/utils/posthog';
import { useUserUid } from '@/hooks/useUserUid';
import { useUtmParams } from '@/hooks/useUtmParams';
import { isSpark as isSparkHelper, Product } from '@/utils/productHelpers';
import { buildAppDeepLink, buildMobileRedirectUrl, isSafeRedirectUri } from '@/utils/urlHelpers';
import { AssignmentTypeOfClient } from '@/apollo/__generated__/graphql';
import { logError } from '@/utils/logError';

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  marginTop: 8,
  '& > *': {
    marginTop: 24,
  },
});

const CriteriaWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  marginTop: 20,
});

const Label = styled(Typography)({
  display: 'inline',
});

const List = styled(Box)(({ theme }) => ({
  margin: 0,
  padding: 0,
  listStylePosition: 'inside',
  listStyle: 'disc',
  '& li::marker': {
    color: theme.palette.secondary.main,
  },
}));

type Props = {
  careTeamUid: string;
  token: string;
  product: Product;
  onNext: (authToken: string) => void;
  onError: () => void;
  isGeneric?: boolean;
  // Only PreOnboardingPage's plain-open() browser needs this; other callers already self-close.
  bounceThroughEmailVerification?: boolean;
};

const CreatePassword: React.FC<Props> = ({
  careTeamUid, token, product, onNext, onError, isGeneric = true, bounceThroughEmailVerification = false,
}) => {
  const { t } = useTranslation();
  const { userUid } = useUserUid();
  const location = useLocation();
  const { utmParams } = useUtmParams();
  const { formState: { errors, isValid }, clearErrors, getValues } = useFormContext();
  const [savePasswordAndActivateUser, { loading }] = useMutation(SAVE_PASSWORD_AND_ACTIVATE_USER);

  const [returnError, setReturnError] = useState(false);
  const [visibility, setVisibility] = useState({
    password: false,
    confirmPassword: false,
  });

  const posthogProgram = getProgram(product);
  const posthog = usePostHog();
  // Blocks a second tap while the app switch is still pending after the mutation resolves.
  const hasSubmittedRef = useRef(false);
  // Keeps the button showing loading for that same gap, since mutation's own `loading` clears early.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleVisibility = (field: 'password' | 'confirmPassword') => {
    setVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const getRedirectUri = () => {
    // Check if redirect_uri is present (indicates mobile app flow)
    if (location.search) {
      return new URLSearchParams(location.search)?.get('redirect_uri');
    }
    return null;
  };

  // Security: redirectUri is validated by isSafeRedirectUri() (inside buildMobileRedirectUrl) first.
  const redirectToMobileApp = (
    redirectUri: string,
    authToken: string,
    refreshToken: string,
    userId: string,
    userEmail: string,
  ) => {
    const url = buildMobileRedirectUrl(redirectUri, {
      token: authToken, refreshToken, userId, userEmail,
    });
    if (url) {
      window.location.href = url;
    }
  };

  const onSubmit = async () => {
    // A second tap while a submission is already in flight or pending the app switch is a no-op.
    if (hasSubmittedRef.current) {
      return;
    }
    hasSubmittedRef.current = true;
    setIsSubmitting(true);

    try {
      if (returnError) {
        throw new Error('Intentional error produced by the override toggle');
      }

      const passwordsData = getValues();

      const redirectUri = getRedirectUri();
      // About to attempt the app either way, so Cortex should skip its "ready to download" email.
      const isMobileFlow = !!redirectUri;

      const response = await savePasswordAndActivateUser({
        variables: {
          careTeamUid,
          password: passwordsData.password,
          token,
          clientType: isMobileFlow ? AssignmentTypeOfClient.Mobile : AssignmentTypeOfClient.WebPortal,
          ...utmParams,
        },
      });

      if (!response.data?.savePasswordAndActivateUser.success) {
        logError('[CreatePassword]', 'password not set', response.errors, { level: 'warning' });
        hasSubmittedRef.current = false;
        setIsSubmitting(false);
        onError();
        return;
      }
      const authData = response.data.savePasswordAndActivateUser.authToken;
      const authToken = authData?.token;
      const refreshToken = authData?.refreshToken;
      const userId = authData?.user?.uid;
      const userEmail = authData?.user?.email;

      if (!authToken) {
        logError('[CreatePassword]', 'auth token missing in response', undefined, { level: 'warning' });
        hasSubmittedRef.current = false;
        setIsSubmitting(false);
        onError();
        return;
      }

      // Fired only on successful activation. send_instantly + sendBeacon let
      // the event survive the deep-link navigation to the mobile app below.
      posthog.capture('ActivationFlowCompleted', {
        program: posthogProgram,
        'wellness.cortex_id': userUid,
      }, { send_instantly: true, transport: 'sendBeacon' });

      if (redirectUri) {
        // Mobile app flow: validate and redirect to deep link
        if (!isSafeRedirectUri(redirectUri)) {
          // Log scheme and host only — query/fragment may contain tokens.
          let redactedUri: string;
          try {
            const parsed = new URL(redirectUri);
            redactedUri = `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
          } catch {
            redactedUri = '[unparseable]';
          }
          logError('[CreatePassword]', 'unsafe redirect_uri blocked', redactedUri);
          hasSubmittedRef.current = false;
          setIsSubmitting(false);
          onError();
          return;
        }

        if (bounceThroughEmailVerification) {
          // Bounces through EmailVerification.tsx's openAuth() so the sheet actually dismisses itself.
          const handoffUrl = new URL(window.location.href);
          const handoffHash = new URLSearchParams({ token: authToken });
          if (refreshToken) { handoffHash.set('refresh_token', refreshToken); }
          if (userId) { handoffHash.set('user_id', userId); }
          if (userEmail) { handoffHash.set('user_email', userEmail); }
          // Fragment, not query string: never leaves the browser in the actual HTTPS request openAuth() makes.
          handoffUrl.hash = handoffHash.toString();
          const bounceDeepLink = buildAppDeepLink(product, handoffUrl.toString());
          if (bounceDeepLink) {
            window.location.href = bounceDeepLink;
          } else {
            redirectToMobileApp(redirectUri, authToken, refreshToken, userId, userEmail);
          }
        } else {
          // Already inside an openAuth() session (e.g. EmailVerification.tsx opened it) - no bounce needed.
          redirectToMobileApp(redirectUri, authToken, refreshToken, userId, userEmail);
        }
        // Left true: the app is taking over, a stray extra tap should stay a no-op.
      } else {
        onNext(authToken);
        // Left true: we're navigating away inside CAP, a stray extra tap should stay a no-op.
      }
    } catch (e) {
      logError('[CreatePassword]', 'mutation failed', e, { level: 'warning' });
      hasSubmittedRef.current = false;
      setIsSubmitting(false);
      onError();
    }
  };

  const isSpark = isSparkHelper(product);
  const isMobileAppFlow = !!getRedirectUri();
  let flowType = 'default';
  if (isMobileAppFlow) { flowType = (isGeneric ? 'generic' : 'specific'); }
  const titleKey = isSpark
    ? 'coverage.spark.person_enrolling.spark_title'
    : `coverage.sleepio.patient.you_are_covered.${flowType}.title`;
  const subtitleKey = isSpark
    ? 'coverage.spark.person_enrolling.spark_subtitle'
    : `coverage.sleepio.patient.you_are_covered.${flowType}.subtitle`;
  return (
    <ContentContainer>
      <Box>
        <Typography variant="h2" style={{ marginBottom: 8 }}>
          {t(titleKey)}
        </Typography>
        <Typography>
          {t(subtitleKey)}
        </Typography>
      </Box>
      <Controller
        name="password"
        render={({ field }) => (
          <TextField
            {...field}
            data-testid="create-password-password"
            required
            label={t('coverage.sleepio.patient.password.label')}
            placeholder={t('coverage.sleepio.patient.password.placeholder')}
            helperText={errors?.password?.message}
            error={!!errors?.password}
            onFocus={() => clearErrors('password')}
            type={visibility.password ? 'text' : 'password'}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => toggleVisibility('password')}
                    edge="end"
                  >
                    {visibility.password ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      <Controller
        name="confirmPassword"
        render={({ field }) => {
          const values = getValues();
          const validationState = getPasswordValidationState(values, errors, isValid, t);

          return (
            <TextField
              {...field}
              data-testid="create-password-confirm-password"
              required
              label={t('coverage.sleepio.patient.confirm_password.label')}
              placeholder={t('coverage.sleepio.patient.confirm_password.placeholder')}
              helperText={validationState.errorMessage}
              error={validationState.showError}
              onFocus={() => clearErrors('confirmPassword')}
              type={visibility.confirmPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => toggleVisibility('confirmPassword')}
                      edge="end"
                    >
                      {visibility.confirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          );
        }}
      />
      {!isSpark && (
        <CriteriaWrapper>
          <Label>
            {t('coverage.sleepio.patient.password_criteria.title')}
          </Label>
          <List component="ul">
            <li>
              <Label>{t('coverage.sleepio.patient.password_criteria.size')}</Label>
            </li>
            <li>
              <Label>{t('coverage.sleepio.patient.password_criteria.letter')}</Label>
            </li>
            <li>
              <Label>{t('coverage.sleepio.patient.password_criteria.number')}</Label>
            </li>
            <li>
              <Label>{t('coverage.sleepio.patient.password_criteria.special_char')}</Label>
            </li>
          </List>
        </CriteriaWrapper>
      )}
      <Button
        variant="contained"
        type="submit"
        fullWidth
        role="button"
        data-testid="submit-button"
        color="primary"
        loading={loading || isSubmitting}
        disabled={!isValid || loading || isSubmitting}
        rounded
        onClick={onSubmit}
        style={
          isSpark
            ? {
              backgroundColor: sparkTheme.palette.primary.main,
              color: sparkTheme.palette.getContrastText(sparkTheme.palette.primary.main),
              borderRadius: 100,
            }
            : undefined
        }
      >
        {t(`coverage.sleepio.patient.you_are_covered.${flowType}.button_text`)}
      </Button>
      {process.env.NODE_ENV !== 'production' && (
        <OverrideToggle
          userMessage="Simulate server error"
          override={setReturnError}
        />
      )}
    </ContentContainer>
  );
};

export default CreatePassword;
